import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { FileText, AlertTriangle, CheckCircle, Coins, Grid as GridIcon, List, Download, ChevronUp, ChevronDown, Minus, TrendingUp, TrendingDown, BarChart3, Lightbulb, Clock, Info, Sparkles, Upload, Plus, Send } from 'lucide-react';
import type { AIInvoiceContext, AnalysisResults, DashboardStats, Invoice } from '../types';
import { formatDate } from '../utils/formatters';
import { useInvoiceFiltering, InvoiceForFiltering } from '../hooks/useInvoiceFiltering';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { exportInvoicesToCSV, Invoice as CsvInvoice } from '../utils/csvExport';
import EnhancedFilters from './EnhancedFilters';
import { ViewPointChip } from './dashboard/ViewPointChip';
import StatusWorkflowInfo from './dashboard/StatusWorkflowInfo';
import { useNavigate } from 'react-router-dom';
import { getDashboardStats, getInvoiceStatusLabel, getInvoiceStatusColor } from '../api/invoices';
import { useAppDispatch } from '../store';
import { updateDocumentStatus } from '../store/documentSlice';
import type { InvoiceStatus } from '../types';

// Helper function to extract currency from entities
const extractCurrency = (doc: AnalysisResults): string => {
  const currencyEntity = doc.entities?.['CURRENCY']?.[0]?.text;
  if (currencyEntity) {
    return currencyEntity;
  }

  // Try to infer from amount text
  const amountText = doc.entities?.['TOTAL_AMOUNT']?.[0]?.text ||
                     doc.entities?.['INVOICE_AMOUNT']?.[0]?.text ||
                     doc.entities?.['MONEY']?.[0]?.text || '';

  if (amountText.includes('€') || amountText.toLowerCase().includes('eur')) return '€';
  if (amountText.includes('£') || amountText.toLowerCase().includes('gbp')) return '£';
  if (amountText.includes('¥') || amountText.toLowerCase().includes('jpy')) return '¥';
  if (amountText.includes('$') || amountText.toLowerCase().includes('usd')) return '$';

  return ''; // Unknown currency — show number only
};

// Helper function to format currency
const formatCurrency = (amount: number, currency: string = ''): string => {
  const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return currency ? `${currency}${formatted}` : formatted;
};

// Names the ECB rate day used for out-of-window conversions, degrading to
// wording that stays true if the API couldn't name a date.
const fxRateClause = (iso?: string | null): string => {
  if (!iso) return 'the newest exchange rate available';
  const day = new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  return `the exchange rate of ${day}, the newest available`;
};

// Compact label for a chart bar. It sits in a column about 30px wide, so it has
// to stay short whatever the value: currency conversion produces amounts like
// 57.03025385417874, which used to be printed in full and spill across the chart.
const formatBarLabel = (amount: number): string => {
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(0)}k`;
  return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

/**
 * What an invoice's AI button sends to the assistant (table and grid alike).
 *
 * The prefill is a real question about this invoice; it used to be the
 * button's own label, so the chat box read "Ask about this invoice". The
 * invoiceContext pins the invoice by id — a document id for uploads, the
 * Invoice id for created ones — and goes with every message, so follow-ups
 * stay about it instead of the most recently uploaded document.
 */
const askAIState = (invoice: InvoiceForFiltering) => {
  const isCreated = invoice.sourceType === 'created';
  const filename: string | undefined = invoice.document?.metadata?.filename;
  const number = invoice.invoiceNumber && invoice.invoiceNumber !== 'N/A' ? invoice.invoiceNumber : undefined;
  const name = number ? `invoice ${number}` : filename ? `the invoice in ${filename}` : 'this invoice';
  const known = (value?: string) => (value && value !== 'Unknown' ? value : undefined);
  // The other party: the client on an invoice you issued, the supplier on a bill.
  const counterparty = isCreated || invoice.invoiceDirection === 'outgoing'
    ? known(invoice.buyer)
    : known(invoice.seller) ?? known(invoice.vendor);
  const lowConfidence = invoice.status === 'review'
    && invoice.validationScore !== undefined && invoice.validationScore < 80;

  let prefillMessage: string;
  if (isCreated) {
    prefillMessage = `Summarise ${name}${counterparty ? ` for ${counterparty}` : ''}: the total, the due date, whether it has been paid, and anything that needs my attention.`;
  } else if (invoice.status === 'incomplete') {
    prefillMessage = `Which required fields are missing from ${name}?`;
  } else if (lowConfidence) {
    prefillMessage = `Which fields on ${name} were read with low confidence, and what should I double-check?`;
  } else {
    prefillMessage = `Summarise ${name}: who it's from, the total, the due date, and anything that needs my attention.`;
  }

  return {
    prefillMessage,
    contextData: isCreated
      ? { invoiceNumber: invoice.invoiceNumber, amount: invoice.amount, currency: invoice.currency }
      : {
          documentId: invoice.document?.id,
          filename,
          entities: invoice.document?.entities,
          ...(invoice.status === 'incomplete' && { status: 'incomplete' }),
          ...(lowConfidence && { confidence: invoice.validationScore }),
        },
    source: invoice.status === 'incomplete' ? 'dashboard_missing_fields' : 'dashboard_validation',
    invoiceContext: {
      invoiceNumber: number ?? filename,
      clientName: counterparty,
      // Created invoices carry negative ids on the dashboard so they never
      // collide with document ids.
      ...(isCreated ? { createdInvoiceId: Math.abs(invoice.id) } : { documentId: invoice.id }),
    } as AIInvoiceContext,
  };
};

// ISO code -> display symbol for the reporting-currency totals.
// CAD and AUD get their country prefix: a bare '$' made a 1,000 CAD total
// indistinguishable from 1,000 USD.
const CCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', PLN: 'zł', RON: 'lei', CHF: 'CHF ', CAD: 'CA$', AUD: 'A$', JPY: '¥',
};

interface DashboardOverviewProps {
  documents: AnalysisResults[];
  createdInvoices: Invoice[];
  onDocumentSelect: (document: AnalysisResults) => void;
}

export const DashboardOverview: React.FC<DashboardOverviewProps> = ({ documents, createdInvoices, onDocumentSelect }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // View mode state
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedInvoices, setSelectedInvoices] = useState<Set<number>>(new Set());
  const [showNotification, setShowNotification] = useState(true);

  // API stats state
  const [apiStats, setApiStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Review popup state

  // Fetch dashboard stats from API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const stats = await getDashboardStats();
        setApiStats(stats);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  // Convert uploaded documents to filtering format
  const uploadedInvoicesForFiltering: InvoiceForFiltering[] = useMemo(() => {
    return documents.map(doc => {
      const invoiceNum = doc.entities?.['INVOICE_NUMBER']?.[0]?.text || 'N/A';
      // Seller (renamed from vendor)
      const seller = doc.entities?.['SELLER_NAME']?.[0]?.text ||
                     doc.entities?.['SELLER']?.[0]?.text ||
                     doc.entities?.['VENDOR']?.[0]?.text ||
                     doc.entities?.['ORG']?.[0]?.text || 'Unknown';
      // Buyer
      const buyer = doc.entities?.['BUYER_NAME']?.[0]?.text ||
                    doc.entities?.['BUYER']?.[0]?.text ||
                    doc.entities?.['CUSTOMER']?.[0]?.text || 'Unknown';
      const amountStr = doc.entities?.['TOTAL_AMOUNT']?.[0]?.text ||
                       doc.entities?.['INVOICE_AMOUNT']?.[0]?.text ||
                       doc.entities?.['MONEY']?.[0]?.text || '0';
      const amount = parseFloat(amountStr.replace(/[^\d.\-]/g, '')) || 0;

      const invoiceDate = doc.entities?.['INVOICE_DATE']?.[0]?.text ||
                          doc.entities?.['DATE']?.[0]?.text || '';
      const dueDate = doc.entities?.['DUE_DATE']?.[0]?.text ||
                      doc.entities?.['PAYMENT_DUE']?.[0]?.text || '';

      const allEntities = Object.values(doc.entities || {}).flat();
      const avgConfidence = allEntities.length > 0
        ? allEntities.reduce((sum, e) => sum + e.confidence, 0) / allEntities.length
        : 0;

      const hasRequiredFields = ['INVOICE_NUMBER', 'INVOICE_DATE', 'TOTAL_AMOUNT', 'SELLER_NAME', 'BUYER_NAME']
        .every(type => {
          if (type === 'INVOICE_DATE' && doc.entities?.['DATE']) return true;
          if (type === 'SELLER_NAME' && (doc.entities?.['SELLER'] || doc.entities?.['VENDOR'] || doc.entities?.['ORG'])) return true;
          if (type === 'BUYER_NAME' && (doc.entities?.['BUYER'] || doc.entities?.['CUSTOMER'])) return true;
          if (type === 'TOTAL_AMOUNT' && (doc.entities?.['INVOICE_AMOUNT'] || doc.entities?.['MONEY'])) return true;
          return doc.entities?.[type] && doc.entities[type].length > 0;
        });

      let status = 'incomplete';
      if (hasRequiredFields && avgConfidence >= 0.8) {
        status = 'ready';
      } else if (hasRequiredFields) {
        status = 'review';
      }

      return {
        id: doc.id,
        invoiceNumber: invoiceNum,
        vendor: seller,  // Keep vendor for compatibility but we now call it seller
        seller,
        buyer,
        amount,
        currency: extractCurrency(doc),
        date: invoiceDate ? new Date(invoiceDate) : new Date(),
        dueDate: dueDate ? new Date(dueDate) : undefined,
        status,                                     // data quality: ready/review/incomplete (used for AI chip logic)
        invoiceStatus: doc.invoice_status || null,  // workflow status: ready_to_process/confirmed/paid/overdue
        invoiceDirection: doc.invoice_direction || null,
        validationScore: Math.round(avgConfidence * 100),
        document: doc,
        sourceType: 'uploaded' as const
      };
    });
  }, [documents]);

  // Convert created invoices to filtering format (negative IDs to avoid collision with uploaded doc IDs)
  const createdInvoicesForFiltering: InvoiceForFiltering[] = useMemo(() => {
    return createdInvoices.map(inv => ({
      id: -inv.id,
      invoiceNumber: inv.invoice_number,
      vendor: inv.seller?.name || inv.client?.name || 'Unknown',
      seller: inv.seller?.name || 'Unknown',
      buyer: inv.client?.name || 'Unknown',
      amount: inv.total,
      currency: inv.currency || '',
      date: inv.invoice_date ? new Date(inv.invoice_date) : new Date(inv.created_at || ''),
      dueDate: inv.due_date ? new Date(inv.due_date) : undefined,
      status: 'ready',
      invoiceStatus: inv.status || null,
      invoiceDirection: inv.direction || null,
      // Deliberately absent: a hand-typed invoice was never run through
      // extraction, so it has no confidence to report. This used to be
      // hardcoded to 100 and rendered under "Data Extraction Confidence".
      validationScore: undefined,
      sourceType: 'created' as const,
    }));
  }, [createdInvoices]);

  // Merge uploaded + created into one list for filtering
  const invoicesForFiltering: InvoiceForFiltering[] = useMemo(() => [
    ...uploadedInvoicesForFiltering,
    ...createdInvoicesForFiltering,
  ], [uploadedInvoicesForFiltering, createdInvoicesForFiltering]);

  // Use filtering hook
  const {
    filteredInvoices,
    searchTerm,
    setSearchTerm,
    quickFilters,
    toggleQuickFilter,
    sortConfig,
    updateSort,
    // These were implemented in the hook but never destructured here, so the
    // whole Advanced Filters panel rendered inputs that changed nothing.
    advancedFilters,
    setAdvancedFilters,
    clearAllFilters,
    activeFilterCount
  } = useInvoiceFiltering(invoicesForFiltering);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSearch: () => {
      // Focus search is handled in the hook
    },
    onEscape: () => {
      setSelectedInvoices(new Set());
    },
    onSelectAll: () => {
      if (selectedInvoices.size === filteredInvoices.length) {
        setSelectedInvoices(new Set());
      } else {
        setSelectedInvoices(new Set(filteredInvoices.map(inv => inv.id)));
      }
    }
  });

  // Bulk actions
  const handleToggleSelection = useCallback((id: number) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedInvoices.size === filteredInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filteredInvoices.map(inv => inv.id)));
    }
  }, [filteredInvoices, selectedInvoices.size]);

  // The Status column must carry the same workflow status the table shows.
  // It used to export inv.status, the data-quality flag ('ready' | 'review' |
  // 'incomplete'), so an invoice displayed as Paid exported as "Ready to
  // Process" — in a file people hand to accounting.
  const toCsvRow = useCallback((inv: InvoiceForFiltering): CsvInvoice => ({
    invoiceNumber: inv.invoiceNumber,
    vendor: inv.vendor,
    buyer: inv.buyer,
    amount: inv.amount,
    currency: inv.currency || '',
    date: inv.date,
    dueDate: inv.dueDate,
    status: inv.invoiceStatus
      ? getInvoiceStatusLabel(inv.invoiceStatus as any, inv.invoiceDirection as any)
      : '',
    validationScore: inv.validationScore,
  }), []);

  const handleExportSelected = useCallback(() => {
    const selected = filteredInvoices.filter(inv => selectedInvoices.has(inv.id));
    exportInvoicesToCSV(selected.map(toCsvRow));
    setSelectedInvoices(new Set());
  }, [filteredInvoices, selectedInvoices, toCsvRow]);

  const handleExportAll = useCallback(() => {
    exportInvoicesToCSV(filteredInvoices.map(toCsvRow));
  }, [filteredInvoices, toCsvRow]);

  // Calculate business-focused statistics
  const dashboardStats = useMemo(() => {
    if (documents.length === 0 && createdInvoices.length === 0) return null;

    const validDocuments = documents.filter(doc => doc.metadata && doc.metadata.analysis_timestamp);

    // Helper function to extract amount from entity
    const extractAmount = (text: string): number | null => {
      const cleanStr = text.replace(/[^\d.,\-]/g, '');
      try {
        return parseFloat(cleanStr.replace(',', ''));
      } catch {
        return null;
      }
    };

    // Helper function to check required fields
    const hasRequiredFields = (doc: AnalysisResults): boolean => {
      const entities = doc.entities || {};
      const requiredTypes = ['INVOICE_NUMBER', 'INVOICE_DATE', 'TOTAL_AMOUNT', 'SELLER_NAME', 'BUYER_NAME'];
      return requiredTypes.every(type => {
        // Check for type or common alternatives
        if (type === 'INVOICE_DATE' && entities['DATE']) return true;
        if (type === 'SELLER_NAME' && (entities['SELLER'] || entities['VENDOR'] || entities['ORG'])) return true;
        if (type === 'BUYER_NAME' && (entities['BUYER'] || entities['CUSTOMER'])) return true;
        if (type === 'TOTAL_AMOUNT' && (entities['INVOICE_AMOUNT'] || entities['MONEY'])) return true;
        return entities[type] && entities[type].length > 0;
      });
    };

    // Helper function to get confidence score
    const getConfidenceScore = (doc: AnalysisResults): number => {
      const allEntities = Object.values(doc.entities || {}).flat();
      if (allEntities.length === 0) return 0;
      return allEntities.reduce((sum, e) => sum + e.confidence, 0) / allEntities.length;
    };

    // Categorize documents by status
    const readyToProcess = validDocuments.filter(doc =>
      hasRequiredFields(doc) && getConfidenceScore(doc) >= 0.8
    );

    const needsReview = validDocuments.filter(doc =>
      hasRequiredFields(doc) && getConfidenceScore(doc) < 0.8 && getConfidenceScore(doc) >= 0.6
    );

    const hasMissingFields = validDocuments.filter(doc => !hasRequiredFields(doc));

    // Extract due dates and check overdue — skip already-paid documents
    const now = new Date();
    const overdueDocs = validDocuments.filter(doc => {
      if (doc.invoice_status === 'paid') return false;
      const dueDateEntities = doc.entities?.['DUE_DATE'] || doc.entities?.['PAYMENT_DUE'] || [];
      if (dueDateEntities.length === 0) return false;

      try {
        const dueDateStr = dueDateEntities[0].text;
        const dueDate = new Date(dueDateStr);
        return dueDate < now;
      } catch {
        return false;
      }
    });

    // Calculate total amounts processed this month with currency breakdown
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const thisMonthDocs = validDocuments.filter(doc => {
      const timestamp = new Date(doc.metadata?.analysis_timestamp || '');
      return timestamp >= thisMonthStart;
    });

    // Calculate totals by currency
    const currencyTotals: Record<string, number> = {};
    thisMonthDocs.forEach(doc => {
      const totalEntities = doc.entities?.['TOTAL_AMOUNT'] || doc.entities?.['INVOICE_AMOUNT'] || doc.entities?.['MONEY'] || [];
      if (totalEntities.length === 0) return;

      const amounts = totalEntities.map(e => extractAmount(e.text)).filter(a => a !== null) as number[];
      if (amounts.length === 0) return;

      const currency = extractCurrency(doc);
      const amount = Math.max(...amounts);
      currencyTotals[currency] = (currencyTotals[currency] || 0) + amount;
    });

    // Include created invoices from this month
    const thisMonthCreated = createdInvoices.filter(inv => {
      const invDate = new Date(inv.created_at || '');
      return invDate >= thisMonthStart;
    });
    thisMonthCreated.forEach(inv => {
      if (inv.total) {
        // '' means "currency unknown" and buckets separately. Defaulting to '€'
        // here silently relabelled unknown amounts as euros and summed them
        // into the euro total.
        const currency = inv.currency || '';
        currencyTotals[currency] = (currencyTotals[currency] || 0) + inv.total;
      }
    });

    // Use primary currency (most common) for main display
    const primaryCurrency = Object.keys(currencyTotals).length > 0
      ? Object.keys(currencyTotals).reduce((a, b) => currencyTotals[a] > currencyTotals[b] ? a : b)
      : '';
    const totalAmountThisMonth = currencyTotals[primaryCurrency] || 0;

    // Calculate average extraction accuracy
    const totalConfidence = validDocuments.reduce((sum, doc) => sum + getConfidenceScore(doc), 0);
    const avgAccuracy = validDocuments.length > 0 ? (totalConfidence / validDocuments.length) * 100 : 0;

    // Top sellers by document count, scoped to this month — the card is headed
    // "Top Vendors This Month" but used to count every document ever uploaded.
    const sellerCounts: Record<string, number> = {};
    thisMonthDocs.forEach(doc => {
      const seller = doc.entities?.['SELLER_NAME']?.[0]?.text ||
                     doc.entities?.['SELLER']?.[0]?.text ||
                     doc.entities?.['VENDOR']?.[0]?.text || 'Unknown';
      sellerCounts[seller] = (sellerCounts[seller] || 0) + 1;
    });
    thisMonthCreated.forEach(inv => {
      const seller = inv.seller?.name || 'Unknown';
      sellerCounts[seller] = (sellerCounts[seller] || 0) + 1;
    });

    const topSellers = Object.entries(sellerCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([name, count]) => ({ name, count }));

    // Top buyers, likewise scoped to this month.
    const buyerCounts: Record<string, number> = {};
    thisMonthDocs.forEach(doc => {
      const buyer = doc.entities?.['BUYER_NAME']?.[0]?.text ||
                    doc.entities?.['BUYER']?.[0]?.text ||
                    doc.entities?.['CUSTOMER']?.[0]?.text || 'Unknown';
      buyerCounts[buyer] = (buyerCounts[buyer] || 0) + 1;
    });
    thisMonthCreated.forEach(inv => {
      const buyer = inv.client?.name || 'Unknown';
      buyerCounts[buyer] = (buyerCounts[buyer] || 0) + 1;
    });

    const topBuyers = Object.entries(buyerCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([name, count]) => ({ name, count }));

    // Weekly activity (last 7 days)
    const weeklyActivity: number[] = [];
    for (let i = 6; i >= 0; i--) {
      const dayStart = new Date(now);
      dayStart.setDate(dayStart.getDate() - i);
      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(dayStart);
      dayEnd.setHours(23, 59, 59, 999);

      const uploadCount = validDocuments.filter(doc => {
        const timestamp = new Date(doc.metadata?.analysis_timestamp || '');
        return timestamp >= dayStart && timestamp <= dayEnd;
      }).length;

      const createdCount = createdInvoices.filter(inv => {
        const invDate = new Date(inv.created_at || '');
        return invDate >= dayStart && invDate <= dayEnd;
      }).length;

      weeklyActivity.push(uploadCount + createdCount);
    }

    // Calculate last month stats for trends
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);

    const lastMonthDocs = validDocuments.filter(doc => {
      const timestamp = new Date(doc.metadata?.analysis_timestamp || '');
      return timestamp >= lastMonthStart && timestamp <= lastMonthEnd;
    });

    // Calculate last month totals
    const lastMonthCurrencyTotals: Record<string, number> = {};
    lastMonthDocs.forEach(doc => {
      const totalEntities = doc.entities?.['TOTAL_AMOUNT'] || doc.entities?.['INVOICE_AMOUNT'] || doc.entities?.['MONEY'] || [];
      if (totalEntities.length === 0) return;

      const amounts = totalEntities.map(e => extractAmount(e.text)).filter(a => a !== null) as number[];
      if (amounts.length === 0) return;

      const currency = extractCurrency(doc);
      const amount = Math.max(...amounts);
      lastMonthCurrencyTotals[currency] = (lastMonthCurrencyTotals[currency] || 0) + amount;
    });

    // Include last month's created invoices in totals
    const lastMonthCreated = createdInvoices.filter(inv => {
      const invDate = new Date(inv.created_at || '');
      return invDate >= lastMonthStart && invDate <= lastMonthEnd;
    });
    lastMonthCreated.forEach(inv => {
      if (inv.total) {
        const currency = inv.currency || '';  // unknown buckets separately, not as EUR
        lastMonthCurrencyTotals[currency] = (lastMonthCurrencyTotals[currency] || 0) + inv.total;
      }
    });

    const totalAmountLastMonth = lastMonthCurrencyTotals[primaryCurrency] || 0;

    // Calculate trends
    const amountTrend = totalAmountLastMonth > 0
      ? ((totalAmountThisMonth - totalAmountLastMonth) / totalAmountLastMonth) * 100
      : totalAmountThisMonth > 0 ? 100 : 0;

    const docCountTrend = lastMonthDocs.length > 0
      ? ((thisMonthDocs.length - lastMonthDocs.length) / lastMonthDocs.length) * 100
      : thisMonthDocs.length > 0 ? 100 : 0;

    // Calculate last month accuracy
    const lastMonthConfidence = lastMonthDocs.length > 0
      ? lastMonthDocs.reduce((sum, doc) => sum + getConfidenceScore(doc), 0) / lastMonthDocs.length * 100
      : 0;

    const accuracyTrend = lastMonthConfidence > 0
      ? avgAccuracy - lastMonthConfidence
      : avgAccuracy > 0 ? avgAccuracy : 0;

    const overdueCreated = createdInvoices.filter(inv => inv.status === 'overdue');

    return {
      totalDocuments: validDocuments.length + createdInvoices.length,
      readyToProcess: readyToProcess.length,
      needsReview: needsReview.length,
      missingFields: hasMissingFields.length,
      overdue: overdueDocs.length + overdueCreated.length,
      thisMonthDocs: thisMonthDocs.length + thisMonthCreated.length,
      totalAmountThisMonth,
      primaryCurrency,
      avgAccuracy,
      topSellers,
      topBuyers,
      weeklyActivity,
      readyToProcessDocs: readyToProcess,
      needsReviewDocs: needsReview,
      missingFieldsDocs: hasMissingFields,
      overdueDocs,
      // Trends
      amountTrend,
      docCountTrend,
      accuracyTrend
    };
  }, [documents, createdInvoices]);

  if (!dashboardStats) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-foreground-muted mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-foreground">No Documents Yet</h3>
        <p className="text-foreground-muted mt-2">Upload your first document to see analytics</p>
      </div>
    );
  }

  // Money totals from the API are converted server-side into one reporting
  // currency; show that currency's symbol (fall back to the guessed one while loading).
  const reportingSymbol = apiStats?.reporting_currency
    ? (CCY_SYMBOL[apiStats.reporting_currency] || `${apiStats.reporting_currency} `)
    : dashboardStats.primaryCurrency;

  // One overdue number for the whole screen. The URGENT banner used to read the
  // client-side tally while the tile preferred the API's, and the two counted
  // different things — the backend ignored documents entirely. It now folds them
  // in, so the API value is authoritative and the local tally is only a
  // placeholder while stats load.
  const overdueCount = apiStats?.overdue_count ?? dashboardStats.overdue;

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="dash-header">
        <div className="dash-eyebrow"><span className="dot" />Today at a glance</div>
        <h1>Dashboard</h1>
        <p className="sub">Track status, due dates, and monthly activity</p>
      </div>

      {/* Smart Notification Bar - Priority: overdue > missing > review */}
      {showNotification && (
        <>
          {overdueCount > 0 ? (
            <div className="dash-banner dash-accent-red">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-red-500 dark:text-red-400" />
                </div>
                <div className="text-sm text-red-700 dark:text-red-100">
                  <strong className="font-semibold">⚠️ URGENT:</strong> <strong className="font-semibold">{overdueCount} invoice{overdueCount !== 1 ? 's' : ''}</strong> past due date – immediate action required
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="px-4 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-md text-red-700 dark:text-red-100 text-sm font-medium transition-colors"
                  onClick={() => {
                    toggleQuickFilter('overdue');
                    // Scroll to invoices table
                    document.getElementById('invoices-table')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  View Overdue
                </button>
                <button
                  className="text-red-600/70 dark:text-red-100/70 hover:text-red-700 dark:hover:text-red-100 text-sm transition-colors"
                  onClick={() => setShowNotification(false)}
                >
                  Dismiss ×
                </button>
              </div>
            </div>
          ) : dashboardStats.missingFields > 0 ? (
            <div className="dash-banner dash-accent-amber">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="text-sm text-yellow-700 dark:text-yellow-100">
                  <strong className="font-semibold">{dashboardStats.missingFields} invoice{dashboardStats.missingFields !== 1 ? 's' : ''}</strong> need attention: Missing required fields
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="px-4 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 rounded-md text-yellow-700 dark:text-yellow-100 text-sm font-medium transition-colors"
                  onClick={() => setShowNotification(false)}
                >
                  Dismiss
                </button>
                <button
                  className="text-yellow-600/70 dark:text-yellow-100/70 hover:text-yellow-700 dark:hover:text-yellow-100 text-sm transition-colors"
                  onClick={() => setShowNotification(false)}
                >
                  Dismiss ×
                </button>
              </div>
            </div>
          ) : dashboardStats.needsReview > 0 ? (
            <div className="dash-banner dash-accent-blue">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Info className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-100">
                  <strong className="font-semibold">{dashboardStats.needsReview} invoice{dashboardStats.needsReview !== 1 ? 's' : ''}</strong> pending review: Low confidence scores
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="px-4 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 rounded-md text-blue-700 dark:text-blue-100 text-sm font-medium transition-colors"
                  onClick={() => setShowNotification(false)}
                >
                  Dismiss
                </button>
                <button
                  className="text-blue-600/70 dark:text-blue-100/70 hover:text-blue-700 dark:hover:text-blue-100 text-sm transition-colors"
                  onClick={() => setShowNotification(false)}
                >
                  Dismiss ×
                </button>
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* Metrics Row - Invoice Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Sent */}
        <div
          className="dash-stat dash-accent-amber clickable relative overflow-hidden group"
          onClick={() => toggleQuickFilter('sent')}
        >
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-yellow-600/80 dark:text-yellow-300/70 font-semibold">
              <Send className="w-4 h-4" />
              <span>SENT</span>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity" title="Invoices sent to clients">
              <Info className="w-3.5 h-3.5 text-yellow-500/50 dark:text-yellow-300/50" />
            </div>
          </div>
          <div className="text-4xl font-bold text-gray-900 dark:text-white leading-none mb-1">{statsLoading ? '...' : (apiStats?.sent_count ?? 0)}</div>
          <div className="text-sm text-foreground-muted">invoices</div>
        </div>

        {/* Paid */}
        <div
          className="dash-stat dash-accent-green clickable relative overflow-hidden group"
          onClick={() => toggleQuickFilter('paid')}
        >
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-green-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-green-600/80 dark:text-green-300/70 font-semibold">
              <CheckCircle className="w-4 h-4" />
              <span>PAID</span>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity" title="Invoices that have been paid">
              <Info className="w-3.5 h-3.5 text-green-500/50 dark:text-green-300/50" />
            </div>
          </div>
          <div className="text-4xl font-bold text-gray-900 dark:text-white leading-none mb-1">{statsLoading ? '...' : (apiStats?.paid_count ?? 0)}</div>
          <div className="text-sm text-foreground-muted">invoices</div>
        </div>

        {/* Income This Year */}
        <div className="dash-stat dash-accent-coral relative overflow-hidden group col-span-2 sm:col-span-1">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-coral-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-coral-600/80 dark:text-foreground-muted font-semibold">
              <Coins className="w-4 h-4" />
              <span>THIS YEAR</span>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity" title="Income received this year (same as Money In, but limited to the current year)">
              <Info className="w-3.5 h-3.5 text-coral-500/50 dark:text-foreground-muted" />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white leading-none mb-1">
            {statsLoading ? '...' : formatCurrency(apiStats?.income_this_year ?? 0, reportingSymbol)}
          </div>
          <div className="text-sm text-foreground-muted">income</div>
        </div>

        {/* Monthly Activity Chart */}
        <div className="dash-stat dash-accent-coral relative overflow-hidden group col-span-2 sm:col-span-3 lg:col-span-2">
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-coral-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-coral-600/80 dark:text-foreground-muted font-semibold">
            <BarChart3 className="w-4 h-4" />
            {/* Two different things render below: monthly income when the stats
                API answered, weekly document counts when it didn't. The heading
                has to name whichever one is actually on screen. */}
            <span>{apiStats?.monthly_amounts ? 'MONTHLY INCOME' : 'WEEKLY ACTIVITY'}</span>
          </div>
          <div className="flex justify-between h-40 gap-1 mb-2 border-b border-coral-500/20">
            {apiStats?.monthly_amounts ? (
              Object.entries(apiStats.monthly_amounts).map(([month, amount]) => {
                const maxAmount = Math.max(...Object.values(apiStats.monthly_amounts), 1);
                const height = (amount / maxAmount) * 100;
                return (
                  <div
                    key={month}
                    className="flex-1 flex flex-col items-center justify-end"
                  >
                    {amount > 0 && (
                      <span className="text-[10px] text-coral-600/70 dark:text-foreground-muted mb-1 font-medium whitespace-nowrap">
                        {formatBarLabel(amount)}
                      </span>
                    )}
                    <div
                      className="w-full bg-coral-500/30 rounded-t hover:bg-coral-500/60 transition-all cursor-pointer"
                      style={{ height: `${Math.max(height, 2)}%` }}
                      title={`${month}: ${formatCurrency(amount, reportingSymbol)} received`}
                    />
                  </div>
                );
              })
            ) : (
              dashboardStats.weeklyActivity.map((count, index) => {
                const maxCount = Math.max(...dashboardStats.weeklyActivity, 1);
                const height = (count / maxCount) * 100;
                return (
                  <div
                    key={index}
                    className="flex-1 flex flex-col items-center justify-end"
                  >
                    {count > 0 && (
                      <span className="text-[10px] text-coral-600/70 dark:text-foreground-muted mb-1 font-medium">{count}</span>
                    )}
                    <div
                      className="w-full bg-coral-500/30 rounded-t hover:bg-coral-500/60 transition-all cursor-pointer"
                      style={{ height: `${height}%` }}
                      title={`${count} document${count !== 1 ? 's' : ''}`}
                    />
                  </div>
                );
              })
            )}
          </div>
          <div className="flex justify-between text-xs text-coral-600/50 dark:text-foreground-muted">
            {apiStats?.monthly_amounts ? (
              Object.keys(apiStats.monthly_amounts).map((month) => (
                <span key={month} className="text-[10px] flex-1 text-center">{month}</span>
              ))
            ) : (
              <>
                <span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Processing Pipeline Section - Invoice Status Workflow */}
      <div className="dash-card">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 text-lg font-semibold text-white">
            <span className="text-xl">⚡</span>
            Invoice Processing Pipeline
          </div>
        </div>

        {/* Row 1: Source Types */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {/* Documents Analyzed */}
          <div
            className="stat-tile stat-tile--blue"
            role="button"
            tabIndex={0}
            onClick={() => toggleQuickFilter('uploaded')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleQuickFilter('uploaded'); } }}
            aria-pressed={quickFilters.uploaded}
          >
            <div className="stat-tile-header">
              <Upload className="stat-tile-icon" />
              <div className="stat-tile-label">Documents Analyzed</div>
            </div>
            <div className="stat-tile-value">{statsLoading ? '…' : (apiStats?.uploaded_documents_count ?? documents.length)}</div>
            <div className="stat-tile-caption">PDFs uploaded &amp; analyzed</div>
          </div>

          {/* Manually Created Invoices */}
          <div
            className="stat-tile stat-tile--indigo"
            role="button"
            tabIndex={0}
            onClick={() => toggleQuickFilter('created')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleQuickFilter('created'); } }}
            aria-pressed={quickFilters.created}
          >
            <div className="stat-tile-header">
              <Plus className="stat-tile-icon" />
              <div className="stat-tile-label">Manually Created</div>
            </div>
            <div className="stat-tile-value">{statsLoading ? '…' : (apiStats?.created_count ?? 0)}</div>
            <div className="stat-tile-caption">built from scratch</div>
          </div>

          {/* Total. Every .stat-tile gets cursor:pointer and a hover lift from
              globals.css, so this one advertised itself as interactive while
              having no handler at all. Clearing the filters is the honest
              meaning of "show me everything". */}
          <div
            className="stat-tile stat-tile--gray"
            role="button"
            tabIndex={0}
            onClick={clearAllFilters}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clearAllFilters(); } }}
            title="Show all documents"
          >
            <div className="stat-tile-header">
              <FileText className="stat-tile-icon" />
              {/* total_count is len(invoices) + every row in the documents
                  table — including uploads that are not invoices and ones that
                  never analysed. "Total Invoices" overstated what it counts. */}
              <div className="stat-tile-label">Total Documents</div>
            </div>
            <div className="stat-tile-value">{statsLoading ? '…' : (apiStats?.total_count ?? 0)}</div>
            <div className="stat-tile-caption">uploaded &amp; created</div>
          </div>
        </div>

        {/* Row 2: Status Workflow */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Ready to Process */}
          <div
            className="stat-tile stat-tile--purple"
            role="button"
            tabIndex={0}
            onClick={() => toggleQuickFilter('readyToProcess')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleQuickFilter('readyToProcess'); } }}
            aria-pressed={quickFilters.readyToProcess}
          >
            <div className="stat-tile-header">
              <Clock className="stat-tile-icon" />
              <div className="stat-tile-label">Ready to Process</div>
            </div>
            <div className="stat-tile-value">{statsLoading ? '…' : (apiStats?.ready_to_process_count ?? dashboardStats.readyToProcess)}</div>
            <div className="stat-tile-caption">awaiting review</div>
          </div>

          {/* Confirmed */}
          <div
            className="stat-tile stat-tile--cyan"
            role="button"
            tabIndex={0}
            onClick={() => toggleQuickFilter('confirmed')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleQuickFilter('confirmed'); } }}
            aria-pressed={quickFilters.confirmed}
          >
            <div className="stat-tile-header">
              <CheckCircle className="stat-tile-icon" />
              <div className="stat-tile-label">Confirmed</div>
            </div>
            <div className="stat-tile-value">{statsLoading ? '…' : (apiStats?.confirmed_count ?? 0)}</div>
            <div className="stat-tile-caption">reviewed &amp; confirmed</div>
          </div>

          {/* Overdue */}
          <div
            className="stat-tile stat-tile--red"
            role="button"
            tabIndex={0}
            onClick={() => toggleQuickFilter('overdue')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleQuickFilter('overdue'); } }}
            aria-pressed={quickFilters.overdue}
          >
            <div className="stat-tile-header">
              <AlertTriangle className="stat-tile-icon" />
              <div className="stat-tile-label">Overdue</div>
            </div>
            <div className="stat-tile-value">{statsLoading ? '…' : overdueCount}</div>
            <div className="stat-tile-caption">past due date</div>
          </div>
        </div>
      </div>

      {/* Money In / Out / Net */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Money In */}
        <div className="dash-stat dash-accent-green">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-green-600/80 dark:text-green-300/70 font-semibold mb-3">
            <TrendingUp className="w-4 h-4" />
            <span>Money In</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white leading-none mb-1">
            {statsLoading ? '...' : formatCurrency(apiStats?.money_in ?? 0, reportingSymbol)}
          </div>
          <div className="text-sm text-foreground-muted">received from paid outgoing</div>
        </div>

        {/* Money Out */}
        <div
          className={`dash-stat dash-accent-red${apiStats?.includes_expenses ? ' clickable' : ''}`}
          onClick={apiStats?.includes_expenses ? () => navigate('/expenses') : undefined}
          title={apiStats?.includes_expenses ? 'View expenses' : undefined}
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-red-600/80 dark:text-red-300/70 font-semibold mb-3">
            <TrendingDown className="w-4 h-4" />
            <span>Money Out</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white leading-none mb-1">
            {statsLoading ? '...' : formatCurrency(apiStats?.money_out ?? 0, reportingSymbol)}
          </div>
          <div className="text-sm text-foreground-muted">
            {apiStats?.includes_expenses ? 'paid bills & expenses' : 'spent on paid incoming'}
          </div>
        </div>

        {/* Net */}
        <div className={`dash-stat ${(apiStats?.net ?? 0) >= 0 ? 'dash-accent-green' : 'dash-accent-red'}`}>
          <div className={`flex items-center gap-2 text-xs uppercase tracking-wide font-semibold mb-3 ${(apiStats?.net ?? 0) >= 0 ? 'text-green-600/80 dark:text-green-300/70' : 'text-red-600/80 dark:text-red-300/70'}`}>
            <Coins className="w-4 h-4" />
            <span>Net</span>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white leading-none mb-1">
            {statsLoading ? '...' : formatCurrency(apiStats?.net ?? 0, reportingSymbol)}
          </div>
          <div className={`text-sm ${(apiStats?.net ?? 0) >= 0 ? 'text-foreground-muted' : 'text-red-600/70 dark:text-red-300/70'}`}>money in − money out</div>
        </div>
      </div>

      {/* FX approximation note — amounts older than the ~90-day rate history are
          converted at the newest rate on hand, which is named rather than called
          "today's": it is often a few days old, and it moves as rates land. */}
      {!statsLoading && (apiStats?.fx_fallback_count ?? 0) > 0 && (
        <div className="flex items-center gap-2 text-xs text-foreground-muted">
          <Info className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            {apiStats!.fx_fallback_count} amount{apiStats!.fx_fallback_count === 1 ? '' : 's'} older than 90 days {apiStats!.fx_fallback_count === 1 ? 'was' : 'were'} converted at {fxRateClause(apiStats?.fx_rate_date)}.
          </span>
        </div>
      )}

      {/* Analytics Section - NEW */}
      <div className="grid grid-cols-1 gap-4">
        {/* Top Vendors (Sellers + Buyers) */}
        <div className="dash-card">
          <div className="flex items-center gap-2 text-base font-semibold text-white mb-5">
            <span>📊</span>
            Top Vendors This Month
          </div>

          {/* Top Sellers */}
          <div className="mb-4">
            <p className="text-xs font-medium text-foreground-muted mb-2">Top Sellers</p>
            <div className="space-y-2">
              {dashboardStats.topSellers.slice(0, 2).map((seller, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-coral-500/5 rounded-lg hover:bg-coral-500/10 transition-colors">
                  <div className="w-8 h-8 bg-coral-500/20 rounded-md flex items-center justify-center text-sm flex-shrink-0">
                    🏢
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{seller.name}</div>
                  </div>
                  <div className="text-xl font-semibold text-foreground-muted">{seller.count}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Buyers */}
          <div>
            <p className="text-xs font-medium text-foreground-muted mb-2">Top Buyers</p>
            <div className="space-y-2">
              {dashboardStats.topBuyers.slice(0, 2).map((buyer, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-coral-500/5 rounded-lg hover:bg-coral-500/10 transition-colors">
                  <div className="w-8 h-8 bg-coral-500/20 rounded-md flex items-center justify-center text-sm flex-shrink-0">
                    🏪
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{buyer.name}</div>
                  </div>
                  <div className="text-xl font-semibold text-foreground-muted">{buyer.count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters */}
      <EnhancedFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        quickFilters={quickFilters}
        onQuickFilterToggle={toggleQuickFilter}
        advancedFilters={advancedFilters}
        onAdvancedFiltersChange={setAdvancedFilters}
        activeFilterCount={activeFilterCount}
        onClearAll={clearAllFilters}
      />

      {/* All Documents List */}
      <div id="invoices-table" className="dash-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-coral-500 dark:text-coral-400" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-foreground">
              All Invoices ({filteredInvoices.length})
            </h3>
          </div>
          <div className="flex items-center gap-4">
            {/* Export Button */}
            <button
              onClick={handleExportAll}
              className="flex items-center gap-2 px-4 py-2 rounded-lg
                         bg-coral-500/20 hover:bg-coral-500/30
                         border border-coral-500/30 dark:border-coral-500/20 hover:border-coral-500/40
                         text-coral-600 dark:text-foreground-secondary text-sm font-medium
                         transition-all duration-200"
              aria-label="Export all to CSV"
            >
              <Download className="w-4 h-4" />
              Export
            </button>

            {/* View Toggle */}
            <div className="inline-flex p-0.5 bg-coral-500/10 rounded-lg gap-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={`h-8 px-4 rounded-md text-sm font-medium transition-all duration-200
                           ${viewMode === 'grid'
                             ? 'bg-coral-500/20 dark:bg-white/10 text-coral-700 dark:text-foreground shadow-sm'
                             : 'text-coral-500 dark:text-foreground-muted hover:text-coral-700 dark:hover:text-foreground'
                           }`}
                aria-label="Grid view"
                aria-pressed={viewMode === 'grid'}
              >
                <GridIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`h-8 px-4 rounded-md text-sm font-medium transition-all duration-200
                           ${viewMode === 'table'
                             ? 'bg-coral-500/20 dark:bg-white/10 text-coral-700 dark:text-foreground shadow-sm'
                             : 'text-coral-500 dark:text-foreground-muted hover:text-coral-700 dark:hover:text-foreground'
                           }`}
                aria-label="Table view"
                aria-pressed={viewMode === 'table'}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Table View */}
        {viewMode === 'table' && filteredInvoices.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-coral-500/10">
            <table className="w-full" aria-label="Invoice list">
              <thead>
                <tr className="bg-coral-500/5 border-b border-coral-500/10">
                  <th className="p-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.size === filteredInvoices.length && filteredInvoices.length > 0}
                      onChange={handleSelectAll}
                      className="w-5 h-5 rounded border-2 border-coral-500/30
                                 checked:bg-coral-500 checked:border-coral-500
                                 focus:ring-2 focus:ring-coral-500/20
                                 cursor-pointer transition-all duration-150"
                      aria-label="Select all invoices"
                    />
                  </th>
                  <th
                    className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide cursor-pointer hover:bg-coral-500/10 transition-colors"
                    onClick={() => updateSort('invoiceNumber')}
                    aria-sort={sortConfig.column === 'invoiceNumber' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="flex items-center gap-1">
                      Invoice #
                      {sortConfig.column === 'invoiceNumber' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide cursor-pointer hover:bg-coral-500/10 transition-colors"
                    onClick={() => updateSort('vendor')}
                    aria-sort={sortConfig.column === 'vendor' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="flex items-center gap-1">
                      Seller
                      {sortConfig.column === 'vendor' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                    Buyer
                  </th>
                  <th
                    className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide cursor-pointer hover:bg-coral-500/10 transition-colors"
                    onClick={() => updateSort('amount')}
                    aria-sort={sortConfig.column === 'amount' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="flex items-center gap-1">
                      Amount
                      {sortConfig.column === 'amount' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th
                    className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide cursor-pointer hover:bg-coral-500/10 transition-colors"
                    onClick={() => updateSort('dueDate')}
                    aria-sort={sortConfig.column === 'dueDate' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <div className="flex items-center gap-1">
                      Due Date
                      {sortConfig.column === 'dueDate' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      )}
                    </div>
                  </th>
                  <th className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                    Status
                  </th>
                  <th className="p-3 text-left text-xs font-semibold text-foreground-muted uppercase tracking-wide">
                    AI Assist
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => {
                  const isSelected = selectedInvoices.has(invoice.id);
                  const rowWorkflowStatus = invoice.invoiceStatus as any;
                  const rowWorkflowDirection = invoice.invoiceDirection as any;
                  const rowStatusLabel = rowWorkflowStatus
                    ? getInvoiceStatusLabel(rowWorkflowStatus, rowWorkflowDirection)
                    : '—';
                  const rowStatusColor = rowWorkflowStatus
                    ? getInvoiceStatusColor(rowWorkflowStatus)
                    : 'bg-coral-500/5 text-coral-400 border-coral-500/20';

                  // Handle row click based on source type
                  const handleRowClick = () => {
                    if (invoice.sourceType === 'created') {
                      navigate(`/created-invoices?invoiceId=${Math.abs(invoice.id)}`);
                    } else {
                      navigate(`/uploaded-invoices?documentId=${invoice.id}`);
                    }
                  };

                  return (
                    <tr
                      key={invoice.id}
                      className={`border-b border-coral-500/5 hover:bg-coral-500/5 transition-colors cursor-pointer
                                 ${isSelected ? 'bg-coral-500/10' : ''}`}
                      onClick={handleRowClick}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelection(invoice.id)}
                          className="w-5 h-5 rounded border-2 border-coral-500/30
                                     checked:bg-coral-500 checked:border-coral-500
                                     focus:ring-2 focus:ring-coral-500/20
                                     cursor-pointer transition-all duration-150"
                          aria-label={`Select invoice ${invoice.invoiceNumber}`}
                        />
                      </td>
                      <td className="p-3 text-sm text-foreground font-mono">{invoice.invoiceNumber}</td>
                      <td className="p-3 text-sm text-foreground-secondary">{invoice.seller || invoice.vendor}</td>
                      <td className="p-3 text-sm text-foreground-secondary">{invoice.buyer || 'Unknown'}</td>
                      <td className="p-3 text-sm text-foreground font-mono">
                        {formatCurrency(invoice.amount, invoice.currency)}
                      </td>
                      <td className="p-3 text-sm text-foreground-secondary">
                        {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            {/* Labelled by the money flow, not the document direction: an
                                "IN" badge on a bill you owe read as money coming in. */}
                            {invoice.invoiceDirection === 'incoming' && (
                              <span
                                className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 whitespace-nowrap"
                                title="Incoming invoice — a bill you pay, so it counts as money out"
                              >
                                MONEY OUT
                              </span>
                            )}
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${rowStatusColor}`}>
                              {rowStatusLabel}
                            </span>
                          </div>
                          {/* "0%" would be as false as the old hardcoded 100 —
                              an invoice with no extraction has no score. */}
                          <span className="text-xs text-coral-400 font-mono" title="Data Extraction Confidence">
                            {invoice.validationScore !== undefined ? `${invoice.validationScore}%` : '—'}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 min-w-[150px]" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => navigate('/ai', { state: askAIState(invoice) })}
                          className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs
                                   bg-purple-500/10 dark:bg-purple-500/10 text-purple-600 dark:text-purple-300
                                   border border-purple-500/30 dark:border-purple-500/20
                                   hover:bg-purple-500/20 hover:text-purple-700 dark:hover:text-purple-200
                                   transition-colors duration-150"
                        >
                          <Sparkles className="w-3 h-3" />
                          <span>
                            {invoice.status === 'incomplete'
                              ? 'Fix Missing'
                              : invoice.status === 'review' && invoice.validationScore !== undefined && invoice.validationScore < 80
                              ? 'Improve'
                              : 'Ask AI'}
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInvoices.map((invoice) => {
              const isSelected = selectedInvoices.has(invoice.id);
              const ai = askAIState(invoice);
              // Use invoice workflow status if set; show nothing if unset
              const workflowStatus = invoice.invoiceStatus as any;
              const workflowDirection = invoice.invoiceDirection as any;
              const statusBadgeLabel = workflowStatus
                ? getInvoiceStatusLabel(workflowStatus, workflowDirection)
                : null;
              const statusBadgeColor = workflowStatus
                ? getInvoiceStatusColor(workflowStatus)
                : '';
              // Status shows as a 3px left accent + pill — never as card fill
              const cardAccent = workflowStatus
                ? workflowStatus === 'paid' ? 'dash-accent-green'
                  : workflowStatus === 'overdue' ? 'dash-accent-red'
                  : workflowStatus === 'confirmed' ? 'dash-accent-coral'
                  : 'dash-accent-blue'
                : '';

              // Handle card click based on source type
              const handleCardClick = () => {
                if (invoice.sourceType === 'created') {
                  navigate(`/created-invoices?invoiceId=${Math.abs(invoice.id)}`);
                } else {
                  navigate(`/uploaded-invoices?documentId=${invoice.id}`);
                }
              };

              return (
                <div
                  key={invoice.id}
                  className={`dash-doc-card ${cardAccent}${isSelected ? ' is-selected' : ''}`}
                  onClick={handleCardClick}
                >
                  {/* Selection Checkbox */}
                  <div
                    className="absolute top-3 left-3 z-10"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleSelection(invoice.id)}
                      className="w-5 h-5 rounded border-2 border-coral-500/30
                                 checked:bg-coral-500 checked:border-coral-500
                                 focus:ring-2 focus:ring-coral-500/20
                                 cursor-pointer transition-all duration-150"
                      aria-label={`Select invoice ${invoice.invoiceNumber}`}
                    />
                  </div>

                  <div className="flex items-start justify-between mb-3 pl-8">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate flex-1">
                      {invoice.sourceType === 'created'
                        ? invoice.invoiceNumber
                        : invoice.document?.metadata?.filename || invoice.invoiceNumber}
                    </h4>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                      {invoice.sourceType === 'created' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-coral-500/20 text-coral-400 border border-coral-500/30">CREATED</span>
                      )}
                      {invoice.invoiceDirection === 'incoming' && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 whitespace-nowrap"
                          title="Incoming invoice — a bill you pay, so it counts as money out"
                        >
                          MONEY OUT
                        </span>
                      )}
                      {statusBadgeLabel && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${statusBadgeColor}`}>
                          {statusBadgeLabel}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-foreground-muted">Invoice #:</span>
                      <span className="text-gray-700 dark:text-foreground-secondary font-mono truncate ml-2">{invoice.invoiceNumber}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-foreground-muted">Seller:</span>
                      <span className="text-gray-700 dark:text-foreground-secondary truncate ml-2">{invoice.seller || invoice.vendor}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-foreground-muted">Buyer:</span>
                      <span className="text-gray-700 dark:text-foreground-secondary truncate ml-2">{invoice.buyer || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-foreground-muted">Amount:</span>
                      <span className="text-gray-700 dark:text-foreground-secondary font-mono">
                        {formatCurrency(invoice.amount, invoice.currency)}
                      </span>
                    </div>
                    {invoice.sourceType !== 'created' && (
                      <div className="flex items-center justify-between pt-2 border-t border-coral-500/20">
                        <span className="text-foreground-muted">Data Extraction Confidence:</span>
                        <span className={`text-gray-700 dark:text-foreground-muted font-mono ${invoice.validationScore !== undefined && invoice.validationScore < 80 ? 'text-yellow-600 dark:text-yellow-400' : ''}`}>
                          {invoice.validationScore !== undefined ? `${invoice.validationScore}%` : '—'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ViewPoint AI Button */}
                  <div className="mt-3 pt-3 border-t border-coral-500/20 dark:border-coral-500/20" onClick={(e) => e.stopPropagation()}>
                    {invoice.sourceType === 'created' ? (
                      <ViewPointChip
                        context="validation"
                        promptText="Ask about this invoice"
                        prefillMessage={ai.prefillMessage}
                        contextData={ai.contextData}
                        invoiceContext={ai.invoiceContext}
                      />
                    ) : invoice.status === 'incomplete' ? (
                      <ViewPointChip
                        context="missing_fields"
                        promptText="Find missing required fields"
                        prefillMessage={ai.prefillMessage}
                        contextData={ai.contextData}
                        invoiceContext={ai.invoiceContext}
                      />
                    ) : invoice.status === 'review' && invoice.validationScore !== undefined && invoice.validationScore < 80 ? (
                      <ViewPointChip
                        context="validation"
                        promptText="Improve extraction accuracy"
                        prefillMessage={ai.prefillMessage}
                        contextData={ai.contextData}
                        invoiceContext={ai.invoiceContext}
                      />
                    ) : (
                      <ViewPointChip
                        context="validation"
                        promptText="Ask about this invoice"
                        prefillMessage={ai.prefillMessage}
                        contextData={ai.contextData}
                        invoiceContext={ai.invoiceContext}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {filteredInvoices.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-foreground">No Invoices Found</h3>
            <p className="text-gray-600 dark:text-foreground-muted mt-1">Try adjusting your filters</p>
            <button
              onClick={clearAllFilters}
              className="mt-4 px-4 py-2 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Status Workflow Info - Collapsible Guide at Bottom */}
      <StatusWorkflowInfo defaultExpanded={false} />

      {/* Floating Action Bar (iOS Bottom Sheet Style) */}
      {selectedInvoices.size > 0 && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50
                     flex items-center gap-4 px-6 py-3 rounded-2xl
                     bg-coral-500/95 backdrop-blur-xl
                     shadow-[0_10px_30px_rgba(139,92,246,0.3),0_1px_2px_rgba(0,0,0,0.1)]
                     animate-slideUp"
          role="region"
          aria-label="Bulk actions"
        >
          <span className="text-sm font-semibold text-white">
            {selectedInvoices.size} selected
          </span>

          <button
            onClick={handleExportSelected}
            className="h-9 px-5 rounded-lg bg-white text-coral-500 text-sm font-semibold
                       hover:scale-105 active:scale-95 transition-transform duration-150
                       flex items-center gap-2"
            aria-label="Export selected invoices"
          >
            <Download className="w-4 h-4" />
            Export
          </button>

          <button
            onClick={() => setSelectedInvoices(new Set())}
            className="w-7 h-7 p-0 rounded-full bg-white/20 text-white
                       hover:bg-white/30 transition-colors
                       flex items-center justify-center"
            aria-label="Clear selection"
          >
            <Minus className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* UploadedDocReviewPopup / CreatedDocReviewPopup used to be mounted here.
          Both were gated on state that is only ever set to false/null, so they
          could never open, and CreatedDocReviewPopup's "Confirm Invoice" handler
          was a console.log placeholder that only closed the dialog. The uploaded
          variant is still reachable from the real post-upload flow in
          MainContent; the created variant has no live caller. */}

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, 20px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }

        .animate-slideUp {
          animation: slideUp 300ms ease-out;
        }

        /* iOS-style checkbox */}
        input[type="checkbox"] {
          -webkit-appearance: none;
          appearance: none;
        }

        input[type="checkbox"]:checked {
          background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: center;
          background-size: 100% 100%;
        }
      `}</style>
    </div>
  );
};

export default DashboardOverview;
